import type { SellingKitGuide } from "../data/marketplaces";

export interface SellingKitDraft {
  listingTitle: string;
  description: string;
  idealBuyer: string;
  searchTerms: string[];
  seoTitle: string;
  seoDescription: string;
  shortPitch: string;
  socialPosts: string[];
  launchEmailSubject: string;
  launchEmailBody: string;
  visualPlan: string[];
}

export type SellingKitParseResult =
  | { ok: true; data: SellingKitDraft }
  | { ok: false; message: string };

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function asText(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asTextList(value: unknown, count: number): string[] {
  const items = Array.isArray(value)
    ? value.map(asText).filter(Boolean).slice(0, count)
    : [];
  return [...items, ...Array.from({ length: Math.max(0, count - items.length) }, () => "")];
}

function extractJson(raw: string): string {
  const withoutFence = raw
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "")
    .trim();
  const start = withoutFence.indexOf("{");
  const end = withoutFence.lastIndexOf("}");
  return start >= 0 && end > start ? withoutFence.slice(start, end + 1) : withoutFence;
}

export function parseSellingKitAnswer(
  raw: string,
  guide: SellingKitGuide,
): SellingKitParseResult {
  let parsed: Record<string, unknown>;
  try {
    parsed = asRecord(JSON.parse(extractJson(raw)));
  } catch {
    return {
      ok: false,
      message: "That answer could not be read. Copy the complete AI answer and paste it here again.",
    };
  }

  const email = asRecord(parsed.launchEmail);
  const data: SellingKitDraft = {
    listingTitle: asText(parsed.listingTitle ?? parsed.title),
    description: asText(parsed.description ?? parsed.productDescription),
    idealBuyer: asText(parsed.idealBuyer ?? parsed.audience),
    searchTerms: asTextList(
      parsed.searchTerms ?? parsed.keywords ?? parsed.tags,
      guide.searchCount,
    ),
    seoTitle: asText(parsed.seoTitle),
    seoDescription: asText(parsed.seoDescription),
    shortPitch: asText(parsed.shortPitch),
    socialPosts: asTextList(parsed.socialPosts, 3),
    launchEmailSubject: asText(parsed.launchEmailSubject ?? email.subject),
    launchEmailBody: asText(parsed.launchEmailBody ?? email.body),
    visualPlan: asTextList(
      parsed.visualPlan ?? parsed.imagePlan ?? parsed.screenshotPlan,
      guide.visualCount,
    ),
  };

  if (!data.description || !data.idealBuyer || !data.searchTerms.some(Boolean)) {
    return {
      ok: false,
      message: "The answer is incomplete. Use the copied Paper Kingdom prompt again so the description, buyer, and search terms are included.",
    };
  }

  return { ok: true, data };
}
